// eslint-disable-next-line max-len
import {
  Attendance,
  getAvailableGiustificativi,
  Giustificativo,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
// eslint-disable-next-line max-len
import {
  addGiustificativoToEmployee,
  attendanceToTempAttendance,
} from "@/screens/app/attendances/attendanceUtils";
import Fuse from "fuse.js";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";

type UseAddGiustificativoBottomSheetParams = {
  onClose: () => void;
  employeeAttendance: Attendance;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
};

export function useAddGiustificativoBottomSheet({
  onClose,
  employeeAttendance,
  setIsSaving,
}: UseAddGiustificativoBottomSheetParams) {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    availableGiustificativi,
    attendanceMonth,
    setAttendanceMonth,
    setTempAttendanceMonth,
    setAvailableGiustificativi,
    selectedMonth,
    selectedYear,
  } = useContext(AttendancesContext);
  const [searchQuery, setSearchQuery] = useState("");

  const alreadyAddedGiustificativiShorts =
    employeeAttendance.days[0].giustificativi.map((g) => g.type);

  // eslint-disable-next-line max-len
  const [giustificativiSearchResults, setGiustificativiSearchResults] =
    useState<Giustificativo[]>();

  const giustificativiFuse = availableGiustificativi
    ? new Fuse(availableGiustificativi, { keys: ["description", "type"] })
    : undefined;

  const areAvailable =
    availableGiustificativi && availableGiustificativi.length !== 0;
  useEffect(() => {
    if (availableGiustificativi) {
      setGiustificativiSearchResults(
        availableGiustificativi?.filter(
          (g) => !alreadyAddedGiustificativiShorts.includes(g.type),
        ),
      );
    }
  }, [alreadyAddedGiustificativiShorts, availableGiustificativi]);
  useEffect(() => {
    if (areAvailable) {
      return;
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }

    getAvailableGiustificativi({
      path: { mandate_id: selectedMandateId },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAvailableGiustificativi(data);
      setGiustificativiSearchResults(
        data.filter((g) => !alreadyAddedGiustificativiShorts.includes(g.type)),
      );
    });
  }, [
    areAvailable,
    alreadyAddedGiustificativiShorts,
    lang,
    selectedMandateId,
    setAvailableGiustificativi,
  ]);

  function onChangeSearchQuery(query: string) {
    if (
      !availableGiustificativi ||
      !giustificativiFuse ||
      !employeeAttendance ||
      !attendanceMonth
    )
      return;

    setSearchQuery(query);
    const searchResults = (
      query.length >= 1
        ? giustificativiFuse.search(query).map((g) => g.item)
        : availableGiustificativi
    ).filter(
      (result) => !alreadyAddedGiustificativiShorts.includes(result.type),
    );
    setGiustificativiSearchResults(searchResults);
  }

  function onCloseWrapper() {
    setSearchQuery("");
    onClose();
  }

  function onAddGiustificativo(giustificativo: Giustificativo) {
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      return;
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (!selectedMonth || !selectedYear) return;
    setIsSaving(true);
    const newAttendance = addGiustificativoToEmployee(
      attendanceMonth,
      giustificativo,
      employeeAttendance.employee.matricola,
      employeeAttendance.employee.filiale,
    );
    saveAttendance({
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
      query: {
        draft: true,
      },
    }).then(({ data, error }) => {
      setIsSaving(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
    });
    onCloseWrapper();
  }

  return {
    searchQuery,
    lang,
    onCloseWrapper,
    giustificativiSearchResults,
    onChangeSearchQuery,
    onAddGiustificativo,
  };
}
